(() => {
    'use strict';

    const ROOT = document.documentElement;
    const READY_ATTR = 'data-theme-quick-add-ready';
    const strings = {
        close:
            ROOT.dataset.quickAddClose || '',
        quickAdd:
            ROOT.dataset.quickAddLabel || '',
        addToCart:
            ROOT.dataset.quickAddAddToCart || '',
        viewDetails:
            ROOT.dataset.quickAddViewDetails || '',
        viewProduct:
            ROOT.dataset.quickAddViewProduct || '',
        unavailable:
            ROOT.dataset.quickAddUnavailable || '',
        soldOut:
            ROOT.dataset.quickAddSoldOut || '',
        added:
            ROOT.dataset.quickAddAdded || '',
        addedMessage:
            ROOT.dataset.quickAddAddedMessage || '',
        tryAgain:
            ROOT.dataset.quickAddTryAgain || '',
        addError:
            ROOT.dataset.quickAddAddError || '',
        generalError:
            ROOT.dataset.cartGeneralError || ''
    };
    const TRIGGER_SELECTOR = '[data-quick-add]';
    const LABEL_SELECTOR = '[data-quick-add-trigger-label]';

    if (ROOT.hasAttribute(READY_ATTR)) return;
    ROOT.setAttribute(READY_ATTR, '');

    class QuickAdd {
        constructor() {
            this.modal = null;
            this.dialog = null;
            this.overlay = null;
            this.closeButton = null;
            this.content = null;
            this.form = null;
            this.submitButton = null;

            this.product = null;
            this.productUrl = '';
            this.selectedOptions = [];
            this.selectedVariant = null;

            this.trigger = null;
            this.lastFocusedElement = null;
            this.loading = false;
            this.closeTimer = null;
            this.resetTimers = new Map();

            this.onDocumentClick =
                this.onDocumentClick.bind(this);

            this.onKeydown =
                this.onKeydown.bind(this);

            this.close =
                this.close.bind(this);

            this.onSubmit =
                this.onSubmit.bind(this);

            this.onOptionClick =
                this.onOptionClick.bind(this);
        }

        init() {
            this.createModal();

            document.addEventListener(
                'click',
                this.onDocumentClick
            );

            document.addEventListener(
                'keydown',
                this.onKeydown
            );
        }

        createModal() {
            this.modal =
                document.querySelector(
                    '[data-quick-add-modal]'
                );

            if (!this.modal) {
                this.modal =
                    document.createElement('div');

                this.modal.className =
                    'quick-add';

                this.modal.hidden =
                    true;

                this.modal.setAttribute(
                    'data-quick-add-modal',
                    ''
                );

                this.modal.innerHTML = `
          <div
            class="quick-add__overlay"
            data-quick-add-overlay
            aria-hidden="true"
          ></div>

          <div
            class="quick-add__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="QuickAddHeading"
            tabindex="-1"
            data-quick-add-dialog
          >
            <button
              type="button"
              class="quick-add__close"
              aria-label="${this.escapeAttr(strings.close)}"
              data-quick-add-close
            >
              <span aria-hidden="true">
                ×
              </span>
            </button>

            <div
              class="quick-add__content"
              data-quick-add-content
            ></div>
          </div>
        `;

                document.body.appendChild(
                    this.modal
                );
            }

            this.dialog =
                this.modal.querySelector(
                    '[data-quick-add-dialog]'
                );

            this.overlay =
                this.modal.querySelector(
                    '[data-quick-add-overlay]'
                );

            this.closeButton =
                this.modal.querySelector(
                    '[data-quick-add-close]'
                );

            this.content =
                this.modal.querySelector(
                    '[data-quick-add-content]'
                );

            this.overlay?.addEventListener(
                'click',
                this.close
            );

            this.closeButton?.addEventListener(
                'click',
                this.close
            );
        }

        async onDocumentClick(event) {
            const trigger =
                event.target.closest(
                    TRIGGER_SELECTOR
                );

            if (!trigger) return;

            event.preventDefault();
            event.stopPropagation();

            if (
                trigger.disabled ||
                this.loading ||
                trigger.classList.contains(
                    'is-loading'
                )
            ) {
                return;
            }

            await this.open(
                trigger
            );
        }

        async open(trigger) {
            this.trigger =
                trigger;

            this.lastFocusedElement =
                document.activeElement;

            this.productUrl =
                String(
                    trigger.dataset.productUrl ||
                    ''
                );

            if (!this.productUrl) {
                console.error(
                    '[Quick Add] Missing data-product-url.'
                );

                return;
            }

            this.syncColorScheme(
                trigger
            );

            this.setTriggerLoading(
                true
            );

            try {
                this.product =
                    await this.fetchProduct(
                        this.productUrl
                    );

                if (!this.product) {
                    throw new Error(
                        'Unable to load product.'
                    );
                }

                const availableVariants =
                    this.getAvailableVariants(
                        this.product
                    );

                if (
                    !availableVariants.length
                ) {
                    throw new Error(
                        'This product has no available variants.'
                    );
                }

                if (
                    this.isSingleVariantProduct(
                        this.product
                    )
                ) {
                    await this.addVariant(
                        availableVariants[0],
                        true
                    );

                    return;
                }

                this.renderProduct();
                this.show();
            } catch (error) {
                this.handleError(
                    error
                );
            } finally {
                this.setTriggerLoading(
                    false
                );
            }
        }

        async fetchProduct(productUrl) {
            const cleanUrl =
                productUrl
                    .split('?')[0]
                    .replace(
                        /\/+$/,
                        ''
                    );

            const response =
                await fetch(
                    `${cleanUrl}.js`,
                    {
                        headers: {
                            Accept:
                                'application/json'
                        }
                    }
                );

            if (!response.ok) {
                throw new Error(
                    `Unable to load product (${response.status}).`
                );
            }

            const data =
                await response.json();

            return (
                data?.product &&
                    typeof data.product ===
                    'object'
                    ? data.product
                    : data
            );
        }

        isAvailable(value) {
            if (
                typeof value ===
                'undefined'
            ) {
                return true;
            }

            return (
                value === true ||
                value === 'true' ||
                value === 1 ||
                value === '1'
            );
        }

        getAvailableVariants(product) {
            if (
                !Array.isArray(
                    product?.variants
                )
            ) {
                return [];
            }

            return product.variants.filter(
                (variant) =>
                    variant?.id &&
                    this.isAvailable(
                        variant.available
                    )
            );
        }

        isSingleVariantProduct(product) {
            if (
                !Array.isArray(
                    product?.variants
                ) ||
                product.variants.length !==
                1
            ) {
                return false;
            }

            if (
                !Array.isArray(
                    product.options
                ) ||
                product.options.length ===
                0
            ) {
                return true;
            }

            if (
                product.options.length !==
                1
            ) {
                return false;
            }

            const option =
                product.options[0];

            const values =
                Array.isArray(
                    option?.values
                )
                    ? option.values
                    : [];

            return (
                String(
                    option?.name ||
                    ''
                ).toLowerCase() ===
                'title' &&
                values.length === 1 &&
                this.optionValueName(
                    values[0]
                ).toLowerCase() ===
                'default title'
            );
        }

        renderProduct() {
            const product =
                this.product;

            const options =
                Array.isArray(
                    product?.options
                )
                    ? product.options
                    : [];

            const initialVariant =
                this.getAvailableVariants(
                    product
                )[0] ||
                product?.variants?.[0] ||
                null;

            this.selectedOptions =
                new Array(
                    options.length
                );

            this.selectedVariant =
                null;

            if (
                initialVariant &&
                Array.isArray(
                    initialVariant.options
                ) &&
                initialVariant.options
                    .length ===
                options.length
            ) {
                initialVariant.options.forEach(
                    (
                        value,
                        index
                    ) => {
                        this.selectedOptions[
                            index
                        ] = value;
                    }
                );
            }

            const imageUrl =
                this.variantImage(
                    initialVariant
                ) ||
                this.productImage(
                    product
                );

            const imageHtml =
                imageUrl
                    ? `
            <img
              class="quick-add__image"
              src="${this.escapeAttr(
                        this.sizedImageUrl(
                            imageUrl,
                            1400
                        )
                    )}"
              alt="${this.escapeAttr(
                        product.title ||
                        ''
                    )}"
              width="900"
              height="1125"
              loading="eager"
              fetchpriority="high"
              data-quick-add-image
            >
          `
                    : `
            <div
              class="quick-add__image-placeholder"
              aria-hidden="true"
            >
              <span>
                +
              </span>
            </div>
          `;

            const optionsHtml =
                options
                    .map(
                        (
                            option,
                            optionIndex
                        ) => {
                            const values =
                                Array.isArray(
                                    option?.values
                                )
                                    ? option.values
                                    : [];

                            const optionName =
                                String(
                                    option?.name ||
                                    ''
                                );

                            const isColor =
                                this.isColorOption(
                                    optionName
                                );

                            const valuesHtml =
                                values
                                    .map(
                                        (
                                            rawValue
                                        ) => {
                                            const value =
                                                this.optionValueName(
                                                    rawValue
                                                );

                                            const available =
                                                this.optionValueAvailable(
                                                    optionIndex,
                                                    value
                                                );

                                            const selected =
                                                this.selectedOptions[
                                                optionIndex
                                                ] ===
                                                value;

                                            const swatchStyle =
                                                isColor
                                                    ? `style="--quick-add-swatch:${this.escapeAttr(
                                                        this.swatchValue(
                                                            rawValue,
                                                            value
                                                        )
                                                    )};"`
                                                    : '';

                                            return `
                        <button
                          type="button"
                          class="
                            quick-add__value
                            ${isColor
                                                    ? 'quick-add__value--color'
                                                    : ''
                                                }
                            ${selected
                                                    ? 'is-selected'
                                                    : ''
                                                }
                            ${available
                                                    ? ''
                                                    : 'is-unavailable'
                                                }
                          "
                          data-option-value
                          data-option-index="${optionIndex}"
                          data-option-value-name="${this.escapeAttr(
                                                    value
                                                )}"
                          aria-pressed="${selected}"
                          aria-disabled="${available
                                                    ? 'false'
                                                    : 'true'
                                                }"
                          ${swatchStyle}
                        >
                          ${isColor
                                                    ? `
                                <span
                                  class="quick-add__swatch"
                                  aria-hidden="true"
                                ></span>
                              `
                                                    : ''
                                                }

                          <span
                            class="quick-add__value-label"
                          >
                            ${this.escapeHtml(
                                                    value
                                                )}
                          </span>
                        </button>
                      `;
                                        }
                                    )
                                    .join('');

                            return `
                <fieldset
                  class="quick-add__option${isColor
                                    ? ' quick-add__option--color'
                                    : ''
                                }"
                  data-option-index="${optionIndex}"
                >
                  <legend
                    class="quick-add__option-title"
                  >
                    ${this.escapeHtml(
                                    optionName
                                )}
                  </legend>

                  <div
                    class="quick-add__values"
                  >
                    ${valuesHtml}
                  </div>
                </fieldset>
              `;
                        }
                    )
                    .join('');

            const productUrl =
                this.productPageUrl();

            const priceHtml =
                this.priceHtml(
                    initialVariant
                );

            this.content.innerHTML = `
        <div
          class="quick-add__layout"
        >
          <div
            class="quick-add__media"
          >
            <a
              class="quick-add__media-link"
              href="${this.escapeAttr(
                productUrl
            )}"
              aria-label="View ${this.escapeAttr(
                product.title ||
                ''
            )}"
            >
              ${imageHtml}
            </a>
          </div>

          <div
            class="quick-add__purchase"
          >
            <header
              class="quick-add__header"
            >
              <p
                class="quick-add__context"
              >
                Quick add
              </p>

              <h2
                id="QuickAddHeading"
                class="quick-add__title"
              >
                ${this.escapeHtml(
                product.title ||
                ''
            )}
              </h2>

              <div
                class="quick-add__price"
                data-quick-add-price
              >
                ${priceHtml}
              </div>
            </header>

            <div
              class="quick-add__options"
            >
              ${optionsHtml}
            </div>

            <div
              class="quick-add__status"
              aria-live="polite"
              aria-atomic="true"
              data-quick-add-status
            ></div>

            <form
              class="quick-add__form"
              data-quick-add-form
            >
              <button
                type="submit"
                class="quick-add__submit"
                data-quick-add-submit
                disabled
              >
                <span
                  data-quick-add-submit-label
                >
                  Add to cart
                </span>
              </button>
            </form>

            <a
              class="quick-add__details-link"
              href="${this.escapeAttr(
                productUrl
            )}"
            >
              <span>
                View full details
              </span>

              <span
                aria-hidden="true"
              >
                →
              </span>
            </a>
          </div>
        </div>
      `;

            this.form =
                this.content.querySelector(
                    '[data-quick-add-form]'
                );

            this.submitButton =
                this.content.querySelector(
                    '[data-quick-add-submit]'
                );

            this.form?.addEventListener(
                'submit',
                this.onSubmit
            );

            this.content.addEventListener(
                'click',
                this.onOptionClick
            );

            this.updateOptionAvailability();
            this.syncSelectedButtons();
            this.updateVariant();
        }

        optionValueName(rawValue) {
            if (
                rawValue &&
                typeof rawValue ===
                'object'
            ) {
                return String(
                    rawValue.name ??
                    rawValue.value ??
                    rawValue.title ??
                    ''
                );
            }

            return String(
                rawValue ??
                ''
            );
        }

        isColorOption(name) {
            const value =
                String(
                    name ||
                    ''
                )
                    .trim()
                    .toLowerCase();

            return (
                value === 'color' ||
                value === 'colour'
            );
        }

        swatchValue(
            rawValue,
            fallback
        ) {
            if (
                rawValue &&
                typeof rawValue ===
                'object' &&
                rawValue.swatch?.color
            ) {
                return String(
                    rawValue.swatch.color
                );
            }

            return (
                String(
                    fallback ||
                    ''
                )
                    .trim()
                    .toLowerCase()
                    .replace(
                        /\s+/g,
                        ''
                    ) ||
                'transparent'
            );
        }

        onOptionClick(event) {
            const button =
                event.target.closest(
                    '[data-option-value]'
                );

            if (
                !button ||
                button.getAttribute(
                    'aria-disabled'
                ) === 'true'
            ) {
                return;
            }

            const optionIndex =
                Number(
                    button.dataset.optionIndex
                );

            const value =
                button.dataset
                    .optionValueName;

            if (
                Number.isNaN(
                    optionIndex
                ) ||
                typeof value ===
                'undefined'
            ) {
                return;
            }

            this.selectedOptions[
                optionIndex
            ] = value;

            for (
                let index =
                    optionIndex + 1;
                index <
                this.selectedOptions.length;
                index++
            ) {
                this.selectedOptions[
                    index
                ] = undefined;
            }

            this.selectedVariant =
                null;

            if (
                this.submitButton
            ) {
                this.submitButton.disabled =
                    true;
            }

            this.syncSelectedButtons();
            this.setStatus('');
            this.updateOptionAvailability();
            this.updateVariant();
        }

        syncSelectedButtons() {
            this.content
                ?.querySelectorAll(
                    '[data-option-value]'
                )
                .forEach(
                    (button) => {
                        const index =
                            Number(
                                button.dataset
                                    .optionIndex
                            );

                        const selected =
                            this.selectedOptions[
                            index
                            ] ===
                            button.dataset
                                .optionValueName;

                        button.classList.toggle(
                            'is-selected',
                            selected
                        );

                        button.setAttribute(
                            'aria-pressed',
                            String(
                                selected
                            )
                        );
                    }
                );
        }

        optionValueAvailable(
            optionIndex,
            value
        ) {
            return (
                this.getAvailableVariants(
                    this.product
                ).some(
                    (variant) => {
                        if (
                            !Array.isArray(
                                variant.options
                            )
                        ) {
                            return false;
                        }

                        if (
                            variant.options[
                            optionIndex
                            ] !== value
                        ) {
                            return false;
                        }

                        for (
                            let index = 0;
                            index <
                            optionIndex;
                            index++
                        ) {
                            const selected =
                                this.selectedOptions[
                                index
                                ];

                            if (
                                typeof selected !==
                                'undefined' &&
                                variant.options[
                                index
                                ] !==
                                selected
                            ) {
                                return false;
                            }
                        }

                        return true;
                    }
                )
            );
        }

        updateOptionAvailability() {
            this.content
                ?.querySelectorAll(
                    '[data-option-value]'
                )
                .forEach(
                    (button) => {
                        const optionIndex =
                            Number(
                                button.dataset
                                    .optionIndex
                            );

                        const value =
                            button.dataset
                                .optionValueName;

                        const available =
                            this.optionValueAvailable(
                                optionIndex,
                                value
                            );

                        const selected =
                            this.selectedOptions[
                            optionIndex
                            ] ===
                            value;

                        const unavailable =
                            !available &&
                            !selected;

                        button.classList.toggle(
                            'is-unavailable',
                            unavailable
                        );

                        button.setAttribute(
                            'aria-disabled',
                            unavailable
                                ? 'true'
                                : 'false'
                        );
                    }
                );
        }

        updateVariant() {
            const options =
                Array.isArray(
                    this.product?.options
                )
                    ? this.product.options
                    : [];

            if (
                options.some(
                    (
                        _,
                        index
                    ) =>
                        typeof this
                            .selectedOptions[
                        index
                        ] ===
                        'undefined'
                )
            ) {
                this.selectedVariant =
                    null;

                if (
                    this.submitButton
                ) {
                    this.submitButton.disabled =
                        true;
                }

                this.setStatus('');

                return;
            }

            const variant =
                (
                    this.product?.variants ||
                    []
                ).find(
                    (candidate) => {
                        return (
                            Array.isArray(
                                candidate?.options
                            ) &&
                            candidate.options.every(
                                (
                                    value,
                                    index
                                ) =>
                                    value ===
                                    this.selectedOptions[
                                    index
                                    ]
                            )
                        );
                    }
                );

            this.selectedVariant =
                variant ||
                null;

            if (!variant) {
                if (
                    this.submitButton
                ) {
                    this.submitButton.disabled =
                        true;
                }

                this.setStatus(
                    'This combination is unavailable.'
                );

                return;
            }

            if (
                !this.isAvailable(
                    variant.available
                )
            ) {
                if (
                    this.submitButton
                ) {
                    this.submitButton.disabled =
                        true;
                }

                this.setStatus(
                    'This variant is sold out.'
                );

                this.updateVariantPresentation(
                    variant
                );

                return;
            }

            if (
                this.submitButton
            ) {
                this.submitButton.disabled =
                    false;
            }

            this.setStatus('');

            this.updateVariantPresentation(
                variant
            );
        }

        updateVariantPresentation(
            variant
        ) {
            const price =
                this.content?.querySelector(
                    '[data-quick-add-price]'
                );

            const image =
                this.content?.querySelector(
                    '[data-quick-add-image]'
                );

            const imageUrl =
                this.variantImage(
                    variant
                );

            if (price) {
                price.innerHTML =
                    this.priceHtml(
                        variant
                    );
            }

            if (
                image &&
                imageUrl
            ) {
                image.src =
                    this.sizedImageUrl(
                        imageUrl,
                        1400
                    );
            }
        }

        priceHtml(variant) {
            const price =
                Number(
                    variant?.price ??
                    this.product?.price ??
                    0
                );

            const compareAt =
                Number(
                    variant?.compare_at_price ??
                    this.product
                        ?.compare_at_price ??
                    0
                );

            const currentPrice =
                this.escapeHtml(
                    this.formatMoney(
                        price
                    )
                );

            if (
                compareAt >
                price
            ) {
                return `
          <span
            class="quick-add__price-current"
          >
            ${currentPrice}
          </span>

          <s
            class="quick-add__price-compare"
          >
            ${this.escapeHtml(
                    this.formatMoney(
                        compareAt
                    )
                )}
          </s>
        `;
            }

            return `
        <span
          class="quick-add__price-current"
        >
          ${currentPrice}
        </span>
      `;
        }

        productPageUrl() {
            return String(
                this.product?.url ||
                this.productUrl ||
                '#'
            );
        }

        productImage(product) {
            const featured =
                product?.featured_image;

            if (
                typeof featured ===
                'string'
            ) {
                return featured;
            }

            if (
                featured &&
                typeof featured ===
                'object'
            ) {
                return String(
                    featured.src ||
                    featured.url ||
                    ''
                );
            }

            const firstImage =
                Array.isArray(
                    product?.images
                )
                    ? product.images[0]
                    : null;

            if (
                typeof firstImage ===
                'string'
            ) {
                return firstImage;
            }

            return (
                firstImage &&
                    typeof firstImage ===
                    'object'
                    ? String(
                        firstImage.src ||
                        firstImage.url ||
                        ''
                    )
                    : ''
            );
        }

        variantImage(variant) {
            const image =
                variant?.featured_image ||
                variant?.featured_media
                    ?.preview_image ||
                variant?.featured_media;

            if (
                typeof image ===
                'string'
            ) {
                return image;
            }

            return (
                image &&
                    typeof image ===
                    'object'
                    ? String(
                        image.src ||
                        image.url ||
                        ''
                    )
                    : ''
            );
        }

        sizedImageUrl(
            source,
            width
        ) {
            if (!source) {
                return '';
            }

            try {
                const normalized =
                    String(
                        source
                    ).startsWith('//')
                        ? `https:${source}`
                        : String(
                            source
                        );

                const url =
                    new URL(
                        normalized,
                        window.location.origin
                    );

                url.searchParams.set(
                    'width',
                    String(
                        width
                    )
                );

                return url.toString();
            } catch {
                return String(
                    source
                );
            }
        }

        async onSubmit(event) {
            event.preventDefault();

            if (
                this.loading ||
                !this.selectedVariant ||
                !this.isAvailable(
                    this.selectedVariant
                        .available
                )
            ) {
                return;
            }

            await this.addVariant(
                this.selectedVariant,
                false
            );
        }

        async addVariant(
            variant,
            direct = false
        ) {
            if (
                !variant?.id ||
                this.loading
            ) {
                return;
            }

            this.loading =
                true;

            if (
                this.submitButton
            ) {
                this.submitButton.disabled =
                    true;

                this.submitButton
                    .classList.add(
                        'is-loading'
                    );
            }

            this.trigger?.classList.add(
                'is-loading'
            );

            try {
                const root =
                    window.Shopify?.routes
                        ?.root ||
                    '/';

                const response =
                    await fetch(
                        `${root}cart/add.js`,
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type':
                                    'application/json',
                                Accept:
                                    'application/json'
                            },
                            body:
                                JSON.stringify({
                                    items: [
                                        {
                                            id: Number(
                                                variant.id
                                            ),
                                            quantity: 1
                                        }
                                    ]
                                })
                        }
                    );

                const data =
                    await response.json();

                if (!response.ok) {
                    throw new Error(
                        data?.description ||
                        data?.message ||
                        'Unable to add this product to cart.'
                    );
                }

                document.dispatchEvent(
                    new CustomEvent(
                        'theme:cart:add',
                        {
                            bubbles: true,
                            detail: {
                                source:
                                    'quick-add',
                                item: data,
                                variant,
                                product:
                                    this.product
                            }
                        }
                    )
                );

                document.dispatchEvent(
                    new CustomEvent(
                        'cart:updated',
                        {
                            bubbles: true,
                            detail: data
                        }
                    )
                );

                const feedbackTrigger =
                    this.trigger;

                this.setTriggerStatus(
                    'ADDED',
                    feedbackTrigger
                );

                this.resetTriggerAfterDelay(
                    feedbackTrigger
                );

                if (direct) {
                    return;
                }

                this.setSubmitStatus(
                    'ADDED'
                );

                this.setStatus(
                    'Added to cart. Keep browsing.'
                );

                window.setTimeout(
                    () =>
                        this.close(),
                    650
                );
            } catch (error) {
                this.handleError(
                    error
                );

                document.dispatchEvent(
                    new CustomEvent(
                        'theme:cart:error',
                        {
                            bubbles: true,
                            detail: {
                                error
                            }
                        }
                    )
                );
            } finally {
                this.loading =
                    false;

                this.submitButton?.classList.remove(
                    'is-loading'
                );

                if (!direct) {
                    this.updateVariant();
                }
            }
        }

        triggerLabel(trigger) {
            return (
                trigger?.querySelector(
                    LABEL_SELECTOR
                ) ||
                trigger?.querySelector(
                    '.card-product__quick-add-label'
                ) ||
                trigger?.querySelector(
                    'span:last-child'
                )
            );
        }

        setTriggerLoading(loading) {
            if (!this.trigger) {
                return;
            }

            this.trigger.classList.toggle(
                'is-loading',
                loading
            );

            this.trigger.disabled =
                loading;
        }

        setTriggerStatus(
            label,
            trigger = this.trigger
        ) {
            if (!trigger) {
                return;
            }

            const labelElement =
                this.triggerLabel(
                    trigger
                );

            if (labelElement) {
                labelElement.textContent =
                    label;
            }

            trigger.classList.toggle(
                'is-added',
                label === 'ADDED'
            );

            trigger.classList.remove(
                'is-loading'
            );

            trigger.disabled =
                false;
        }

        resetTriggerAfterDelay(
            trigger = this.trigger
        ) {
            if (!trigger) {
                return;
            }

            const existing =
                this.resetTimers.get(
                    trigger
                );

            if (existing) {
                window.clearTimeout(
                    existing
                );
            }

            const originalLabel =
                trigger.dataset
                    .quickAddOriginalLabel ||
                'Quick add';

            const timer =
                window.setTimeout(
                    () => {
                        if (
                            document.contains(
                                trigger
                            )
                        ) {
                            const labelElement =
                                this.triggerLabel(
                                    trigger
                                );

                            if (labelElement) {
                                labelElement.textContent =
                                    originalLabel;
                            }

                            trigger.classList.remove(
                                'is-added',
                                'is-loading'
                            );

                            trigger.disabled =
                                false;
                        }

                        this.resetTimers.delete(
                            trigger
                        );
                    },
                    1500
                );

            this.resetTimers.set(
                trigger,
                timer
            );
        }

        setSubmitStatus(label) {
            const labelElement =
                this.submitButton?.querySelector(
                    '[data-quick-add-submit-label]'
                );

            if (labelElement) {
                labelElement.textContent =
                    label;
            }
        }

        show() {
            window.clearTimeout(
                this.closeTimer
            );

            this.modal.hidden =
                false;

            document.body.classList.add(
                'quick-add-open'
            );

            document.dispatchEvent(
                new CustomEvent(
                    'theme:overlay:open',
                    {
                        bubbles: true,
                        detail: {
                            source:
                                'quick-add'
                        }
                    }
                )
            );

            void this.modal.offsetWidth;

            window.requestAnimationFrame(
                () => {
                    this.modal.classList.add(
                        'is-open'
                    );

                    window.setTimeout(
                        () => {
                            this.closeButton?.focus({
                                preventScroll:
                                    true
                            });
                        },
                        50
                    );
                }
            );
        }

        close() {
            if (
                !this.modal ||
                this.modal.hidden
            ) {
                return;
            }

            this.modal.classList.remove(
                'is-open'
            );

            this.modal.classList.add(
                'is-closing'
            );

            window.clearTimeout(
                this.closeTimer
            );

            this.closeTimer =
                window.setTimeout(
                    () => {
                        if (!this.modal) {
                            return;
                        }

                        this.modal.hidden =
                            true;

                        this.modal.classList.remove(
                            'is-closing'
                        );

                        document.body.classList.remove(
                            'quick-add-open'
                        );

                        this.restoreFocus();

                        this.content?.removeEventListener(
                            'click',
                            this.onOptionClick
                        );

                        this.product =
                            null;

                        this.productUrl =
                            '';

                        this.selectedOptions =
                            [];

                        this.selectedVariant =
                            null;

                        this.trigger =
                            null;

                        this.form =
                            null;

                        this.submitButton =
                            null;
                    },
                    250
                );
        }

        onKeydown(event) {
            if (
                !this.modal ||
                this.modal.hidden
            ) {
                return;
            }

            if (
                event.key ===
                'Escape'
            ) {
                event.preventDefault();

                this.close();

                return;
            }

            if (
                event.key ===
                'Tab'
            ) {
                this.trapFocus(
                    event
                );
            }
        }

        trapFocus(event) {
            const focusable =
                [
                    ...this.dialog.querySelectorAll(`
            button:not([disabled]),
            input:not([disabled]),
            select:not([disabled]),
            textarea:not([disabled]),
            a[href],
            [tabindex]:not([tabindex="-1"])
          `)
                ].filter(
                    (element) =>
                        element.offsetParent !==
                        null &&
                        element.getAttribute(
                            'aria-hidden'
                        ) !==
                        'true'
                );

            if (
                !focusable.length
            ) {
                event.preventDefault();

                this.dialog.focus();

                return;
            }

            const first =
                focusable[0];

            const last =
                focusable[
                focusable.length - 1
                ];

            if (
                event.shiftKey &&
                document.activeElement ===
                first
            ) {
                event.preventDefault();

                last.focus();
            } else if (
                !event.shiftKey &&
                document.activeElement ===
                last
            ) {
                event.preventDefault();

                first.focus();
            }
        }

        restoreFocus() {
            if (
                this.lastFocusedElement &&
                document.contains(
                    this.lastFocusedElement
                ) &&
                typeof this
                    .lastFocusedElement
                    .focus ===
                'function'
            ) {
                this.lastFocusedElement.focus({
                    preventScroll:
                        true
                });
            }

            this.lastFocusedElement =
                null;
        }

        setStatus(message) {
            const status =
                this.content?.querySelector(
                    '[data-quick-add-status]'
                );

            if (status) {
                status.textContent =
                    message ||
                    '';
            }
        }

        handleError(error) {
            console.error(
                '[Quick Add]',
                error
            );

            if (
                this.modal &&
                !this.modal.hidden
            ) {
                this.setStatus(
                    error?.message ||
                    document.documentElement.dataset.cartGeneralError ||
                    ''
                );

                if (
                    this.submitButton
                ) {
                    this.submitButton.disabled =
                        false;
                }

                return;
            }

            if (!this.trigger) {
                return;
            }

            const feedbackTrigger =
                this.trigger;

            this.setTriggerStatus(
                'TRY AGAIN',
                feedbackTrigger
            );

            window.setTimeout(
                () => {
                    if (
                        !document.contains(
                            feedbackTrigger
                        )
                    ) {
                        return;
                    }

                    const label =
                        this.triggerLabel(
                            feedbackTrigger
                        );

                    if (label) {
                        label.textContent =
                            feedbackTrigger.dataset
                                .quickAddOriginalLabel ||
                            'Quick add';
                    }

                    feedbackTrigger.classList.remove(
                        'is-added',
                        'is-loading'
                    );

                    feedbackTrigger.disabled =
                        false;
                },
                1500
            );
        }

        formatMoney(cents) {
            if (
                window.Shopify &&
                typeof window.Shopify
                    .formatMoney ===
                'function'
            ) {
                return (
                    window.Shopify.formatMoney(
                        cents,
                        window.Shopify
                            .money_format ||
                        '${{amount}}'
                    )
                );
            }

            return (
                Number(
                    cents
                ) /
                100
            ).toFixed(
                2
            );
        }

        syncColorScheme(trigger) {
            [
                ...this.modal.classList
            ]
                .filter(
                    (className) =>
                        className.startsWith(
                            'color-'
                        )
                )
                .forEach(
                    (className) =>
                        this.modal.classList.remove(
                            className
                        )
                );

            let node =
                trigger;

            while (
                node &&
                node !==
                document.body
            ) {
                const colorClass =
                    [
                        ...node.classList
                    ].find(
                        (className) =>
                            className.startsWith(
                                'color-'
                            )
                    );

                if (colorClass) {
                    this.modal.classList.add(
                        colorClass
                    );

                    return;
                }

                node =
                    node.parentElement;
            }
        }

        escapeHtml(value) {
            return String(
                value ??
                ''
            )
                .replaceAll(
                    '&',
                    '&amp;'
                )
                .replaceAll(
                    '<',
                    '&lt;'
                )
                .replaceAll(
                    '>',
                    '&gt;'
                )
                .replaceAll(
                    '"',
                    '&quot;'
                )
                .replaceAll(
                    "'",
                    '&#039;'
                );
        }

        escapeAttr(value) {
            return this.escapeHtml(
                value
            );
        }
    }

    new QuickAdd().init();
})();